import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getLanguage, t } from 'react-switch-lang';
import { faBars, faSearch } from '@awesome.me/kit-3f20e419a7/icons/classic/solid';
import { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useRouter } from 'next/router';
import { faFileInvoice } from '@awesome.me/kit-3f20e419a7/icons/classic/regular';
import styles from '../styles/NavBar.module.scss';
import Link, { ExternalLink } from './Links';
import { events } from '../utils/Amplitude';
import Button from './Button';
import Announcement from './Announcement';
import { isDevelopment } from '../utils/HostingEnv';
import { setScreenReaderAlert } from '../utils/Accessibility';
import { formatAsTestId } from '../utils/Helpers';

export default function NavBar({ showPaymentInfoButton }) {
  const [showMobileDropDown, setShowMobileDropDown] = useState(false);
  const [showDropDown, setShowDropDown] = useState(false);
  const [query, setQuery] = useState('');
  const router = useRouter();
  const lang = getLanguage();

  const handleSearch = () => {
    const trimmedQuery = query.trim().substring(0, 100);
    router.push(`/partner-search/${encodeURIComponent(trimmedQuery)}`);
    setQuery('');
  };

  const toggleMobileMenu = () => {
    setShowMobileDropDown(!showMobileDropDown);
  };
  return (
    <>
      <nav className={`${styles.nav} noPrint`} id="navbar">
        <div className={styles.menuToggle}>
          <Button
            fill="outline"
            className={styles.hamburgerMenu}
            onClick={toggleMobileMenu}
            ampEvent={events.USER_CLICKED_NAVBAR_HAMBURGER_MENU}
            aria-controls="hamburger-dropdown"
            ariaLabel={t('HamBurgerMenu_ButtonDescription')}
            aria-expanded={showMobileDropDown ? 'true' : 'false'}
            testId={formatAsTestId('navbar hamburger menu', 'button')}
          >
            <FontAwesomeIcon icon={faBars} size="lg" />
          </Button>
        </div>
        <div className={styles.logoContainer}>
          <Link
            href="/"
            className={styles.logo}
            ampEvent={events.USER_CLICKED_NAVBAR_BRAND}
            testId={formatAsTestId('navbar brand', 'link')}
          >
            <Image
              src="/images/logos/PaySimply-Lg.png"
              alt={t('NavBar_Logo_ImageDescription')}
              width={100}
              height={29}
            />
          </Link>
          {isDevelopment && (
          <ExternalLink
            id={styles.siteMapButton}
            noTargetBlank
            href="https://docs.google.com/spreadsheets/d/e/2PACX-1vS0gDYbKGKo-uKldF0Ga3n00Ly-1CCd-S_GiQrwKagicy_ibsa0Omw7nIrIvkUkpoT0xSY_5eJDq4UE/pubhtml?gid=210918116#gid=210918116"
            text="Sandbox"
          />
          )}
        </div>
        {showPaymentInfoButton && (
        <div className={styles.paymentInfoButton}>
          <Button
            fill="outline"
            // the dispatched message event targets the same origin only
            onClick={() => window.postMessage({ type: 'openPaymentInfoModal' })}
            ampEvent={events.USER_OPENED_TRANSACTION_DETAILS_MOBILE}
            ariaLabel={t('PaymentInfo_Open_ButtonDescription')}
            testId={formatAsTestId('transaction details mobile', 'button')}
          >
            <FontAwesomeIcon icon={faFileInvoice} size="2xl" color="#FFFFFF" />
          </Button>
        </div>
        )}
        <div className={`${styles.navContentContainer} ${showMobileDropDown ? styles.show : ''}`} id="hamburger-dropdown">
          <div className={lang === 'fr' ? styles.wideSearchContainer : styles.searchContainer}>
            <label htmlFor="navbar-search" className="sr-only">
              {t('NavBar_Search_Label')}
            </label>
            <input
              id="navbar-search"
              data-testid={formatAsTestId('navbar search', 'input')}
              placeholder={t('NavBar_Search_PlaceHolder')}
              type="text"
              inputMode="search"
              className={`${styles.searchInput} form-control`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
            <Button
              linkBtn
              className={styles.searchIcon}
              onClick={handleSearch}
              ampEvent={events.USER_SEARCHED_BILL_NAVBAR}
              ariaLabel={t('SearchButton_IconDescription')}
              title={t('SearchButton_IconDescription')}
              testId={formatAsTestId('navbar search', 'button')}
            >
              <FontAwesomeIcon
                icon={faSearch}
              />
            </Button>
          </div>
          <Dropdown
            className={styles.dropDown}
            onMouseEnter={() => setShowDropDown(true)}
            onToggle={(nextShow) => setShowDropDown(nextShow)}
            onMouseLeave={() => setShowDropDown(false)}
            show={showDropDown}
            data-testid={formatAsTestId('navbar dropdown', 'dropdown')}
          >
            <Dropdown.Toggle
              id="dropbtn"
              className={styles.dropdownDisplay}
            >
              <span>{t('NavBar_Link_MyPayment')}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu
              className={styles.dropDownItems}
            >
              <Dropdown.Item
                as={Link}
                unsetDefaultClass
                href="/my-payment"
                aria-label={t('NavBar_DropDown_PaymentStatus_LinkDescription')}
                ampEvent={events.USER_CLICKED_NAVBAR_MY_PAYMENT}
                ampEventProps={{ Link: t('NavBar_DropDown_PayMentStatus', null, 'en') }}
                data-testid={formatAsTestId('navbar my payment status', 'link')}
              >
                {t('NavBar_DropDown_PayMentStatus')}
              </Dropdown.Item>
              <Dropdown.Item
                as={Link}
                unsetDefaultClass
                href="/my-payment"
                aria-label={t('NavBar_DropDown_Receipt_LinkDescription')}
                ampEvent={events.USER_CLICKED_NAVBAR_MY_PAYMENT}
                ampEventProps={{ Link: t('NavBar_DropDown_Receipt', null, 'en') }}
                data-testid={formatAsTestId('navbar my payment receipt', 'link')}
              >
                {t('NavBar_DropDown_Receipt')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button
            onClick={() => {
              const widgetButton = document.getElementById('widget-button');
              widgetButton.click();
              setScreenReaderAlert(t('HelpCentre_Widget_ScreenReaderAlert'));
            }}
            linkBtn
            text={t('NavBar_Link_HelpCentre')}
            className={styles.navLink}
            ampEvent={events.USER_CLICKED_NAVBAR_HELP}
            ariaLabel={t('HelpCentre_Widget_ButtonDescription')}
            testId={formatAsTestId('navbar help centre', 'button')}
          />
        </div>
      </nav>
      <Announcement />
    </>
  );
}
